import { SearchOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Divider, Form, Input, message, Row, Select } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { IListColumn, ISortColumn, PaginationResponse } from 'src/@types/search-params';
import {
  getAllDataPackageNew,
  getAssignedPackageHistory,
} from 'src/api/register_and_cancel_and_load_package';
import TableContent from 'src/components/CTable';
import useWindowSize from 'src/hooks/useWindowSize';
import { useAppSelector } from 'src/redux/hook';
import { setGlobalState } from 'src/redux/slice/globalSlice';
import { historyOrderStatus } from 'src/shared/common/package_assigned';
import { IConnectionBatch } from 'src/shared/interface/connection';
import { IOrderStatus, IPackageAssigned } from 'src/shared/interface/package_assigned';
import {
  exportExcel,
  formatDate,
  formatMoney,
  nonAccentVietnamese,
  parseQueryString,
  renderSubType,
} from 'src/utils';
import { renderOptionSubType } from 'src/utils/subType';
import CGridView from 'src/components/CGridView';
import { usePermission } from 'src/hooks/usePermission';

const AssignedPackageHistoryPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const merchantInfo: string | null = localStorage.getItem('merchantInfo');
  const [data, setData] = useState<PaginationResponse<[IConnectionBatch]>>();
  const [sortColumn] = useState<ISortColumn>();
  const [_, setSearchParams] = useSearchParams();
  const { openTabs, activeKeyId } = useAppSelector((state) => state.global);
  const params = location.search;
  const size = useWindowSize();

  const [dataPackage, setDataPackage] = useState<IOrderStatus[]>([]);
  const { PERMISSIONS, hasPermission } = usePermission();

  useEffect(() => {
    fetchGetPackageAssigned();
    fetchListAssignedPackageHistory();
    form.resetFields();
  }, [activeKeyId]);

  const fetchGetPackageAssigned = async () => {
    await getAllDataPackageNew().then((res: any) => {
      if (res) {
        const dataPackage: IOrderStatus[] = [{ value: '', label: 'Tất cả' }];
        res?.map((item: any) => {
          dataPackage?.push({
            label: item.mbfName ?? '',
            value: item?.mbfCode ?? '',
          });
        });
        setDataPackage(dataPackage);
      }
    });
  };

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && current >= moment().endOf('day');
  };

  const handleFormSearch = (value: any) => {
    let values = { ...value };
    values = {
      ...values,
      dateFrom:
        value.dateRange && value?.dateRange[0]
          ? value?.dateRange[0].format('YYYY-MM-DD')
          : value.dateFrom ?? undefined,
      dateTo:
        value.dateRange && value?.dateRange[1]
          ? value?.dateRange[1].format('YYYY-MM-DD')
          : value.dateTo ?? undefined,
      dateRange: undefined,
      search: value.search?.trim(),
    };
    Object.keys(values).forEach((key) => {
      if ([null, '', undefined].includes(values[key])) {
        delete values[key];
      }
    });
    setSearchParams(values);
    fetchListAssignedPackageHistory(values);
  };

  const fetchListAssignedPackageHistory = async (params = {}) => {
    const res = await getAssignedPackageHistory(params);
    if (res) {
      setData(res);
    }
  };

  const handleDetailOrder = (order: any) => {
    dispatch(
      setGlobalState({
        activeKeyMenu: 'order-assigned-package/detail' + '?id=' + order.orderId,
      }),
    );
    if (!openTabs.find((tab) => tab.key === 'order-assigned-package/detail' + '?id=' + order.id)) {
      dispatch(
        setGlobalState({
          openTabs: [
            ...openTabs,
            {
              label: 'Xem chi tiết gán gói ' + order.orderId,
              children: '',
              key: 'order-assigned-package/detail' + '?id=' + order.orderId,
              closable: true,
            },
          ],
        }),
      );
    }
    navigate(`/management/order-assigned-package/detail?id=${order.orderId}`);
  };

  const handleExport = () => {
    const link = document.createElement('a');
    link.target = '_blank';
    link.download = `lich_su_gan_goi_${moment().format('DDMMYYYYHHmmss')}.xlsx`;
    const url =
      process.env.REACT_APP_API_URL + '/api/Provider/register-package/history/export' + params;
    exportExcel(url)
      .then((res) => {
        link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
        link.click();
      })
      .catch((error) => {
        if (error.status === 403) {
          message.error(`Tài khoản của bạn không có quyền truy cập vào chức năng này`);
        }
      });
  };

  const columns: IListColumn[] = [
    { name: 'STT', fieldName: 'index', width: 20, isHideSort: true },
    {
      name: 'Mã đơn',
      fieldName: 'orderId',
      renderSpecialCard: (record) => {
        return (
          <button
            disabled={!hasPermission(PERMISSIONS.ASSIGN_PACKAGE.HISTORY_VIEW_DETAIL)}
            className={`underline md:whitespace-nowrap cursor-pointer bg-transparent border-transparent ${
              !hasPermission(PERMISSIONS.ASSIGN_PACKAGE.HISTORY_VIEW_DETAIL)
                ? 'text-gray-400'
                : 'text-blue-400'
            } font-semibold text-right`}
            onClick={() => handleDetailOrder(record)}
          >
            {record.orderId ?? ''}
          </button>
        );
      },
      isHideSort: true,
    },
    {
      name: 'SIM Serial',
      fieldName: 'serial',
      isHideSort: true,
    },
    {
      name: 'Mã gói',
      fieldName: 'mbfCode',
      isHideSort: true,
    },
    {
      name: 'Tên gói',
      fieldName: 'mbfName',
      isHideSort: true,
    },

    {
      name: 'Loại thuê bao',
      fieldName: 'subType',
      isHideSort: true,
      renderSpecialCard: (record) => {
        return <p>{renderSubType('', record?.subType)}</p>;
      },
    },
    {
      name: 'Thành tiền',
      fieldName: 'price',
      isHideSort: true,
      renderSpecialCard: (record: IPackageAssigned) => {
        return <span>{record.price ? formatMoney(record.price, 'đ') : 0}</span>;
      },
    },
    { name: 'Trạng thái', fieldName: 'statusString', isHideSort: true },
    { name: 'Mã lỗi', fieldName: 'errorCode', isHideSort: true },
    { name: 'Mô tả lỗi', fieldName: 'errorMessage', isHideSort: true },
    {
      name: 'Ngày tạo',
      fieldName: 'createdAt',
      renderSpecialCard: (record) => {
        return <span>{record.createdAt ? formatDate(record.createdAt) : ''}</span>;
      },
    },
    { name: 'Người tạo', fieldName: 'createdBy' },
    {
      name: 'Ngày cập nhật cuối',
      fieldName: 'updatedAt',
      renderSpecialCard: (record) => {
        return <span>{record.updatedAt ? formatDate(record.updatedAt) : ''}</span>;
      },
    },
    { name: 'Người cập nhật cuối', fieldName: 'updatedBy' },
  ];

  return (
    <div>
      <Form
        onFinish={handleFormSearch}
        layout={size.width < 768 ? 'vertical' : 'inline'}
        className='gap-2'
      >
        <Form.Item name='search'>
          <Input
            prefix={<SearchOutlined />}
            placeholder='Mã đơn, Sim Serial'
            className='w-full md:w-[400px]'
            allowClear
          />
        </Form.Item>

        <Form.Item name='mbfCode' label='Gói'>
          <Select
            showSearch
            className='min-w-[150px]'
            placeholder='Vui lòng chọn'
            defaultValue={''}
            options={dataPackage}
            filterOption={(input, option) => {
              return nonAccentVietnamese(
                (option!.label as unknown as string).toString()?.toLowerCase(),
              ).includes(nonAccentVietnamese(input?.toLowerCase()?.trim()));
            }}
          ></Select>
        </Form.Item>
        <Form.Item name='subType' label='Loại thuê bao'>
          <Select
            showSearch
            className='min-w-[200px]'
            placeholder='Vui lòng chọn'
            defaultValue={''}
            options={renderOptionSubType(true)}
            filterOption={(input, option) => {
              return nonAccentVietnamese(
                (option!.label as unknown as string).toString()?.toLowerCase(),
              ).includes(nonAccentVietnamese(input?.toLowerCase()?.trim()));
            }}
          ></Select>
        </Form.Item>
        <Form.Item name='statusString' label='Trạng thái'>
          <Select
            showSearch
            className='min-w-[150px]'
            placeholder='Vui lòng chọn'
            defaultValue={''}
            options={historyOrderStatus}
          ></Select>
        </Form.Item>
        <Form.Item name='createdBy' label='Người tạo'>
          <Select
            showSearch
            className='min-w-[150px]'
            defaultValue={merchantInfo ? JSON.parse(merchantInfo).merchantName : ''}
          >
            <Select.Option value='merchantName'>
              {merchantInfo ? JSON.parse(merchantInfo).merchantName : ''}
            </Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name='dateRange' label='Ngày tạo'>
          <DatePicker.RangePicker
            disabledDate={disabledDate}
            placeholder={['Từ ngày', 'đến ngày']}
            format={['DD/MM/YYYY', 'DD/MM/YYYY']}
          />
        </Form.Item>
        <div className='row-button' style={{ justifyContent: 'flex-end', margin: '1.5rem 0' }}>
          <Button type='primary' htmlType='submit'>
            Tìm kiếm
          </Button>

          {hasPermission(PERMISSIONS.ASSIGN_PACKAGE.HISTORY_EXPORT) && (
            <Button onClick={handleExport}>Export</Button>
          )}
        </div>
      </Form>
      <div className='mb-10 lg:mb-0 mt-6'>
        {size.width < 1024 ? (
          <CGridView
            length={data?.results.length}
            totalRecord={data?.rowCount ?? 0}
            columns={columns}
            title={() => (
              <div className='text-xl font-bold mb-6'>Danh sách lịch sử đơn gán gói</div>
            )}
          >
            {data?.results && data?.results.length > 0 ? (
              data?.results.map((item: IPackageAssigned) => {
                return (
                  <div key={'data-row-' + item.id}>
                    <Divider className='border-t-black' />
                    <Row className='mb-6 items-center' gutter={[4, 16]}>
                      {columns.map((ele) => {
                        if (ele.fieldName !== 'index' && ele.fieldName !== 'action') {
                          return (
                            <>
                              <Col span={10} className={`font-bold label`}>
                                <div className='flex gap-2 items-center'>{ele.name}</div>
                              </Col>
                              <Col className='break-words text-right' span={14}>
                                {ele?.renderSpecialCard
                                  ? ele?.renderSpecialCard(item, null, null, null)
                                  : item[`${ele?.fieldName}`]}
                              </Col>
                            </>
                          );
                        } else if (ele.fieldName === 'action') {
                          return (
                            <Col key={'column-' + item.id + ele.fieldName} span={24}>
                              <div className='flex gap-6'>
                                {ele?.renderSpecialCard
                                  ? ele?.renderSpecialCard(item, null, null, null)
                                  : item[`${ele?.fieldName}`]}
                              </div>
                            </Col>
                          );
                        } else {
                          return <></>;
                        }
                      })}
                    </Row>
                  </div>
                );
              })
            ) : (
              <p>{'Không có dữ liệu'}</p>
            )}
          </CGridView>
        ) : (
          <TableContent
            bordered
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'Không tìm thấy kết quả' }}
            dataSource={data?.results ?? []}
            columns={columns}
            totalRecord={data?.rowCount ?? 0}
            sortColumn={sortColumn}
            onPageChange={(pageNumber, pageSize) => {
              const pagination = parseQueryString(location.search);
              fetchListAssignedPackageHistory({
                ...pagination,
                pageIndex: pageNumber ?? 1,
                pageSize: pageSize ?? 10,
                sortField: pagination.sortField ?? undefined,
                asc: pagination.asc ?? undefined,
              });
            }}
            onSort={(sort: ISortColumn) => {
              const pagination = parseQueryString(location.search);
              fetchListAssignedPackageHistory({
                ...pagination,
                pageIndex: pagination.pageIndex ?? 1,
                pageSize: pagination.pageSize ?? 10,
                sortField: sort.fieldName,
                asc:
                  sort.orderType === 'asc' ? true : sort.orderType === 'desc' ? 'false' : undefined,
              });
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AssignedPackageHistoryPage;
